import { ClipboardList, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useGlobalContext } from '@/store';
import { apiClient } from '@/api/client';
import { ConsultationStepper } from '@/components/ConsultationStepper';
import { useRecaptcha } from '../useRecaptcha';
import { SymptomBlockPager } from '@/components/SymptomBlockPager';
import { reorderQuestionsByWeight, reorderUnvisitedQuestions } from '@/utils/symptomReordering';
import { logTemporaryRemedies } from '@/utils/logTemporaryRemedies';
import { getOrCreateE2EEKey } from '@/utils/e2ee';

interface IntakeOption {
  label: string;
  symptom_ref: string;
}

interface IntakeQuestion {
  group: string;
  question: string;
  option_a: IntakeOption;
  option_b: IntakeOption;
}

export function Intake() {
  const { patient, setToast, setErrorModal } = useGlobalContext();
  const navigate = useNavigate();
  const { getRequestConfig, getErrorMessage } = useRecaptcha();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState<IntakeQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  
  // Pagination and target validation states
  const [blockSize, setBlockSize] = useState<number>(5);
  const [targetCount, setTargetCount] = useState<number>(5);
  const [autoAdvance, setAutoAdvance] = useState<boolean>(true);
  const [currentBlock, setCurrentBlock] = useState<number>(0);
  const [validatedSymptoms, setValidatedSymptoms] = useState<string[]>([]);
  const [symptomRemedyWeights, setSymptomRemedyWeights] = useState<Record<string, Record<string, number>>>({});
  const [symptomRemedyCounts, setSymptomRemedyCounts] = useState<Record<string, number>>({});
  const [linkedByRemedySymptoms, setLinkedByRemedySymptoms] = useState<string[]>([]);
  const [top5Remedies, setTop5Remedies] = useState<string[]>([]);
  const [symptomScores, setSymptomScores] = useState<Record<string, number>>({});
  const [polarDetails, setPolarDetails] = useState<Record<string, any>>({});
  const [visitedBlocks, setVisitedBlocks] = useState<Set<number>>(new Set([0]));

  const fetchedRef = useRef<string>('');

  const handleBlockChange = useCallback((newBlock: number) => {
    if (!visitedBlocks.has(newBlock)) {
      setQuestions((prevQuestions) =>
        reorderUnvisitedQuestions(
          prevQuestions,
          blockSize,
          visitedBlocks,
          (q) => [q.option_a.symptom_ref, q.option_b.symptom_ref],
          validatedSymptoms,
          Object.values(answers),
          symptomRemedyWeights,
          symptomRemedyCounts,
          linkedByRemedySymptoms,
          { onlyPriority2: true, symptomScores, top5Remedies, polarDetails }
        )
      );
      setVisitedBlocks((prev) => new Set([...prev, newBlock]));
    }
    setCurrentBlock(newBlock);
  }, [answers, blockSize, symptomRemedyCounts, symptomRemedyWeights, linkedByRemedySymptoms, validatedSymptoms, visitedBlocks, symptomScores, top5Remedies, polarDetails]);

  const hasRequiredPatientData =
    !!patient.email?.trim() && !!patient.name?.trim() && !!patient.consultationToken;

  useEffect(() => {
    getOrCreateE2EEKey().catch(() => {});
  }, []);

  useEffect(() => {
    apiClient
      .get('/symptoms/validation-config')
      .then((res) => {
        if (res.data?.block_size) setBlockSize(res.data.block_size);
        if (res.data?.target_affirmative_step5) setTargetCount(res.data.target_affirmative_step5);
        if (typeof res.data?.auto_advance_on_target === 'boolean') {
          setAutoAdvance(res.data.auto_advance_on_target);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!hasRequiredPatientData) {
      setErrorModal({
        title: 'Missing patient data',
        message: 'Please complete Patient Account and start a new consultation before Intake.',
      });
      navigate('/account');
      return;
    }

    const consultationKey = `${patient.email}_${patient.consultationToken}`;
    if (fetchedRef.current === consultationKey) {
      return;
    }

    const fetchQuestions = async () => {
      fetchedRef.current = consultationKey;
      setLoading(true);
      try {
        const recaptchaConfig = await getRequestConfig('start_consultation');
        const response = await apiClient.post('/intake/generate-questions', {
          email: patient.email,
          name: patient.name,
          age: patient.age,
          gender: patient.gender,
          country: 'Unknown',
          date_0: new Date().toISOString().slice(0, 10),
          consultation_token: patient.consultationToken,
        }, recaptchaConfig);

        const valSyms = response.data.validated_symptoms || [];
        const weightsMap = response.data.symptom_remedy_weights || {};
        const countsMap = response.data.symptom_remedy_counts || {};
        const linkedSyms = response.data.linked_by_remedy_symptoms || [];
        const top5 = response.data.top5_remedies || [];
        const scores = response.data.symptom_scores || {};
        const details = response.data.polar_details || {};

        setValidatedSymptoms(valSyms);
        setSymptomRemedyWeights(weightsMap);
        setSymptomRemedyCounts(countsMap);
        setLinkedByRemedySymptoms(linkedSyms);
        setTop5Remedies(top5);
        setSymptomScores(scores);
        setPolarDetails(details);

        const rawQuestions = (response.data.questions || []) as IntakeQuestion[];

        // Obtener síntomas ya guardados en esta consulta para pre-seleccionar y colocar preguntas elegidas en primer lugar
        let existingSyms: string[] = [];
        try {
          const symRes = await apiClient.get('/patients/symptoms', {
            params: {
              email: patient.email,
              date_0: new Date().toISOString().slice(0, 10),
              consultation_token: patient.consultationToken,
            },
          });
          existingSyms = (symRes.data?.symptoms || [])
            .filter((s: { step?: number }) => s.step === 4 || s.step === 5)
            .map((s: { symptom?: string }) => s.symptom?.trim().toLowerCase() || '');
        } catch (_) {}

        // Determinar respuestas previamente seleccionadas
        const prefilledAnswers: Record<number, string> = {};
        rawQuestions.forEach((q, idx) => {
          const refA = q.option_a.symptom_ref?.trim().toLowerCase();
          const refB = q.option_b.symptom_ref?.trim().toLowerCase();
          if (refA && existingSyms.includes(refA)) {
            prefilledAnswers[idx] = q.option_a.symptom_ref;
          } else if (refB && existingSyms.includes(refB)) {
            prefilledAnswers[idx] = q.option_b.symptom_ref;
          }
        });

        // Reordenar preguntas colocando primero las que tienen respuesta seleccionada
        const selectedQuestions = rawQuestions.filter((_, idx) => !!prefilledAnswers[idx]);
        const unselectedQuestions = rawQuestions.filter((_, idx) => !prefilledAnswers[idx]);

        const orderedUnselected = reorderQuestionsByWeight(
          unselectedQuestions,
          (q) => [q.option_a.symptom_ref, q.option_b.symptom_ref],
          valSyms,
          Object.values(prefilledAnswers),
          weightsMap,
          countsMap,
          linkedSyms,
          { onlyPriority2: true, symptomScores: scores, top5Remedies: top5, polarDetails: details }
        );

        const finalOrdered = [...selectedQuestions, ...orderedUnselected];

        // Reajustar índices de respuestas para coincidir con la lista final ordenada
        const finalAnswers: Record<number, string> = {};
        finalOrdered.forEach((q, idx) => {
          const refA = q.option_a.symptom_ref?.trim().toLowerCase();
          const refB = q.option_b.symptom_ref?.trim().toLowerCase();
          if (refA && existingSyms.includes(refA)) {
            finalAnswers[idx] = q.option_a.symptom_ref;
          } else if (refB && existingSyms.includes(refB)) {
            finalAnswers[idx] = q.option_b.symptom_ref;
          }
        });

        setQuestions(finalOrdered);
        setAnswers(finalAnswers);
        setVisitedBlocks(new Set([0]));
      } catch (err) {
        fetchedRef.current = '';
        setErrorModal({
          title: 'Error',
          message: getErrorMessage(err, 'Could not load initial questions. Please check your connection.'),
        });
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [hasRequiredPatientData, navigate, setErrorModal, patient.email, patient.consultationToken, patient.name, patient.age, patient.gender, getRequestConfig, getErrorMessage]);

  const saveSymptoms = useCallback(async (symptomsToSave: string[]) => {
    if (!hasRequiredPatientData) {
      setErrorModal({
        title: 'Missing patient data',
        message: 'Please complete Patient Account and start a new consultation before continuing.',
      });
      navigate('/account');
      return;
    }

    setSubmitting(true);
    try {
      const recaptchaConfig = await getRequestConfig('intake_submit');
      await apiClient.post('/intake/save', {
        patient: {
          email: patient.email,
          name: patient.name,
          age: patient.age,
          gender: patient.gender,
          date_0: new Date().toISOString().slice(0, 10),
          consultation_token: patient.consultationToken,
        },
        selected_symptoms: symptomsToSave,
      }, recaptchaConfig);
      await logTemporaryRemedies('Symptom Selection (Intake)', 5, patient);
      setToast({ type: 'success', message: 'Initial symptoms saved.' });
      navigate('/results');
    } catch (err) {
      setErrorModal({ title: 'Error', message: getErrorMessage(err, 'Error saving symptoms.') });
    } finally {
      setSubmitting(false);
    }
  }, [getErrorMessage, getRequestConfig, hasRequiredPatientData, patient, setErrorModal, setToast, navigate]);

  const handleSubmit = useCallback(() => {
    saveSymptoms(Object.values(answers));
  }, [answers, saveSymptoms]);

  // Habilitar deselección al pulsar la misma opción elegida
  const handleToggleAnswer = (globalIndex: number, value: string) => {
    setAnswers((prev) => {
      const current = prev[globalIndex];
      const updated = { ...prev };
      if (current === value) {
        delete updated[globalIndex];
      } else {
        updated[globalIndex] = value;
      }
      const newAffirmativeCount = Object.keys(updated).length;
      if (autoAdvance && newAffirmativeCount >= targetCount && !submitting) {
        setTimeout(() => {
          saveSymptoms(Object.values(updated));
        }, 100);
      }
      return updated;
    });
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl">
        <ConsultationStepper />
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body items-center text-center py-12">
            <Loader2 size={32} className="animate-spin text-primary" />
            <p className="mt-2 text-base-content/70">Generating personalized questionnaire...</p>
          </div>
        </div>
      </div>
    );
  }

  const affirmativeCount = Object.keys(answers).length;

  const totalBlocks = Math.ceil(questions.length / blockSize);
  const safeBlock = Math.min(currentBlock, Math.max(0, totalBlocks - 1));
  const currentQuestions = questions.slice(safeBlock * blockSize, (safeBlock + 1) * blockSize);
  const blockStartIndex = safeBlock * blockSize;

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <ConsultationStepper />

      <div className="card bg-base-100 shadow-sm">
        <div className="card-body py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-info text-info-content rounded-full p-3">
                <ClipboardList size={28} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold">Step 5: Symptom Selection</h2>
                  <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-xs px-2.5 py-0.5 rounded-full font-medium border border-emerald-300">
                    🔒 E2EE Encrypted
                  </span>
                </div>
                <p className="text-sm text-base-content/70">
                  Select the option that best describes your condition. Shared health data is protected via End-to-End Encryption under GDPR & HIPAA standards.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {currentQuestions.map((q, i) => {
          const globalIndex = blockStartIndex + i;
          const value = answers[globalIndex] || '';
          const isASelected = value === q.option_a.symptom_ref;
          const isBSelected = value === q.option_b.symptom_ref;
          return (
            <div key={globalIndex} className={`card bg-base-100 shadow-sm transition-all ${value ? 'border-2 border-primary/40' : ''}`}>
              <div className="card-body p-4 gap-2">
                <div className="flex items-center justify-between gap-2 border-b border-base-200 pb-2">
                  <span className="badge badge-sm badge-ghost">{q.group}</span>
                  <span className="text-xs text-base-content/50">#{globalIndex + 1}</span>
                </div>
                <p className="text-sm font-medium leading-snug min-h-[3rem]">{q.question}</p>
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => handleToggleAnswer(globalIndex, q.option_a.symptom_ref)}
                    className={`btn btn-sm justify-start text-left normal-case font-normal ${
                      isASelected ? 'btn-primary' : 'btn-outline border-base-300 hover:btn-primary'
                    }`}
                  >
                    <span className="truncate">{q.option_a.label}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleAnswer(globalIndex, q.option_b.symptom_ref)}
                    className={`btn btn-sm justify-start text-left normal-case font-normal ${
                      isBSelected ? 'btn-primary' : 'btn-outline border-base-300 hover:btn-primary'
                    }`}
                  >
                    <span className="truncate">{q.option_b.label}</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {questions.length > 0 && (
        <SymptomBlockPager
          totalItems={questions.length}
          blockSize={blockSize}
          currentBlock={safeBlock}
          onBlockChange={handleBlockChange}
          affirmativeCount={affirmativeCount}
          targetCount={targetCount}
          onAdvancePhase={handleSubmit}
          advanceButtonText="View final results"
          isSubmitting={submitting}
        />
      )}
    </div>
  );
}
